import { ScreenerFilter } from '@api';
import { Icon } from '@design/components/icon/Icon';
import filterStyles from '@terminal/components/filter/Filter.module.scss';
import { FilterLabel } from '@terminal/components/filter/FilterLabel';
import type {
  FilterVariant,
  IncludeExcludeLabels,
} from '@terminal/components/filter/include-exclude/include-exclude.types';
import { getFilterOptionsBySelectionMode } from '@terminal/components/filter/include-exclude/utils/include-exclude.utils';
import type { ActiveFilterOptions } from '@terminal/components/filters/Filters';
import { isExcludableSectorFilter } from '@terminal/components/filters/utils/filter-variant.utils';
import {
  getIncludeExcludeSectorOptions,
  getSectorActiveOptions,
} from '@terminal/components/search-tree-modal/utils';
import { FilterUseType } from '@terminal/components/update-filter-options/updateFilterOptions';
import { isFilterWithChildOptionActive } from '@terminal/views/screener/use-screener-store/filter-utils';
import clsx from 'clsx';

import styles from './FilterTree.module.scss';

export interface FilterTreeProps {
  filter: ScreenerFilter;
  filterOptionGroups: ActiveFilterOptions[];
  includeExcludeLabels?: IncludeExcludeLabels;
  variant?: FilterVariant;
  useType?: FilterUseType;
  showTriggerLabel?: boolean;
  multiselect?: boolean;
  id?: string;
  onClick: () => void;
  isModalOpen?: boolean;
}

export const FilterTree = ({
  filter,
  id = filter.key,
  filterOptionGroups,
  includeExcludeLabels,
  variant = 'default',
  useType,
  multiselect = useType === 'single-use' && filter.config.multiselect,
  showTriggerLabel,
  onClick,
  isModalOpen,
}: FilterTreeProps) => {
  const { name } = filter;
  const isExcludableFilter =
    variant === 'include-exclude-multiselect' &&
    isExcludableSectorFilter(filter);
  const { includedOptions, excludedOptions } =
    getFilterOptionsBySelectionMode(filterOptionGroups);
  const { includedSectorOptions, excludedSectorOptions } = isExcludableFilter
    ? getIncludeExcludeSectorOptions({
        options: filter.config.options,
        includedOptions,
        excludedOptions,
      })
    : { includedSectorOptions: [], excludedSectorOptions: [] };
  const displayedFilterOptions = isExcludableFilter
    ? [...includedSectorOptions, ...excludedSectorOptions]
    : getSectorActiveOptions({
        options: filter.config.options,
        activeOptions: includedOptions,
      });
  const isParentOptionActive = isFilterWithChildOptionActive(
    displayedFilterOptions
  );
  const isFilterActive = useType === 'single-use' && isParentOptionActive;

  return (
    <div>
      <div
        className={clsx(styles.filterTreeTrigger, {
          [styles.active]: isFilterActive,
          [styles.showAsHovered]: isModalOpen,
        })}
        data-filter={id}
        data-testid="tree-filter-trigger"
        role="button"
        onClick={onClick}
      >
        {(showTriggerLabel || !isFilterActive) && (
          <span
            className={filterStyles.triggerLabel}
            data-trigger-label
            data-testid="filter-name"
          >
            {name}
          </span>
        )}
        {isFilterActive && (
          <FilterLabel
            options={displayedFilterOptions}
            multiselect={multiselect}
            includedOptions={includedSectorOptions}
            excludedOptions={excludedSectorOptions}
            includeExcludeLabels={includeExcludeLabels}
            variant={isExcludableFilter ? variant : 'default'}
          />
        )}
        <Icon
          iconName="ChevronLightDown"
          size={14}
          fillColor="var(--icon-soft)"
        />
      </div>
    </div>
  );
};
